import { useInfiniteQuery, useMutation, useQuery } from '@tanstack/react-query';
import { api } from '@/modules/common/api/index.ts';
import { useGetCurrentBusinessId } from '@/modules/common/hooks/useGetCurrentBusinessId.ts';
import { CURRENT_USER_CLIENTS_LIST_QUERY_KEY } from '@/modules/common/queries/clients.ts';
import { queryClient } from '@/modules/common/query.ts';
import { withErrorHandling } from '@/modules/common/utils/error-utils.tsx';
import { DOCUMENTS_TREE_QK } from '@/modules/admin-dashboard/queries/documents-folders.ts';
import { CURRENT_USER_TASKS_ACTIVITY_LOGS_QUERY_KEY } from '@/modules/admin-dashboard/queries/tasks.ts';
import {
  CompleteUserDocumentUploadRequest,
  CompleteUserDocumentUploadResponse,
  CreatePublicDocumentLinkV1UserDocumentsDocumentIdPublicLinkPostParams,
  DeletePublicDocumentLinkV1UserDocumentsDocumentIdPublicLinkDeleteParams,
  GetDocumentsV1UserDocumentsGetParams,
  GetPublicDocumentV1UserDocumentsPublicPublicDocumentIdGetParams,
  InitUserDocumentUploadRequest,
  InitUserDocumentUploadResponse,
  UpdateUserDocumentRequest,
} from 'compliance-api';

export const DOCUMENTS_LIST_QUERY_KEY = 'vestra-compliance/documents-list';

type UploadDocumentMutationPayload = Pick<
  InitUserDocumentUploadRequest,
  'name' | 'description' | 'mime_type' | 'file_size' | 'folder_id' | 'client_id'
> & {
  description?: string;
};

export const useGetDocumentsListQuery = (
  payload: GetDocumentsV1UserDocumentsGetParams,
  disabled = false,
) => {
  const { page, ...data } = payload;

  return useInfiniteQuery({
    queryKey: [DOCUMENTS_LIST_QUERY_KEY, JSON.stringify(data), useGetCurrentBusinessId()],
    staleTime: 1000 * 60 * 60 * 3, // 3 hours
    queryFn: async ({ pageParam = 0 }) => {
      const response = await api.v1.getDocumentsV1UserDocumentsGet({
        ...data,
        page: pageParam || page,
      });
      return response.data;
    },
    keepPreviousData: true,
    retry: 2,
    getNextPageParam: ({ total, page, page_size }) => {
      const totalPage = Math.ceil(total / page_size);
      const hasMore = totalPage > page;
      if (hasMore) {
        return page + 1;
      }
    },
    enabled: !disabled,
  });
};

export const useInitDocumentUploadMutation = () => {
  return useMutation({
    mutationFn: async (
      payload: UploadDocumentMutationPayload,
    ): Promise<InitUserDocumentUploadResponse> => {
      const initPayload: InitUserDocumentUploadRequest = {
        name: payload.name,
        description: payload.description,
        mime_type: payload.mime_type,
        file_size: payload.file_size,
        folder_id: payload.folder_id,
        client_id: payload.client_id,
      };

      const initResponse = await api.v1.initUploadDocumentV1UserDocumentsUploadInitPost(
        initPayload,
      );
      return initResponse.data;
    },
    retry: 0,
  });
};

export const uploadDocumentToStorage = async (
  initResponseData: InitUserDocumentUploadResponse,
  file: File,
) => {
  if (!initResponseData.presigned_upload_fields) {
    throw new Error('Missing presigned upload fields');
  }

  const formData = new FormData();
  Object.entries(initResponseData.presigned_upload_fields).forEach(([key, value]) => {
    formData.append(key, value);
  });
  formData.append('file', file);

  const response = await fetch(initResponseData.presigned_upload_url, {
    method: 'POST',
    body: formData,
  });

  if (!response.ok) {
    throw new Error(`S3 upload failed with status ${response.status}`);
  }
};

export const useCompleteDocumentUploadMutation = () => {
  return useMutation({
    mutationFn: async (
      context: InitUserDocumentUploadResponse,
    ): Promise<CompleteUserDocumentUploadResponse> => {
      const completePayload: CompleteUserDocumentUploadRequest = {
        document_id: context.document_id,
      };

      const completeResponse = await api.v1.completeUploadDocumentV1UserDocumentsUploadCompletePost(
        completePayload,
      );
      return completeResponse.data;
    },
    retry: 1,
    onSuccess: async () => {
      await queryClient.invalidateQueries([DOCUMENTS_LIST_QUERY_KEY]);
      await queryClient.invalidateQueries([DOCUMENTS_TREE_QK]);
      await queryClient.invalidateQueries([CURRENT_USER_TASKS_ACTIVITY_LOGS_QUERY_KEY]);
      await queryClient.invalidateQueries([CURRENT_USER_CLIENTS_LIST_QUERY_KEY]);
    },
  });
};

export const useCreatePublicDocumentLinkMutation = ({
  onSuccess,
}: {
  onSuccess?: (public_document_id?: string) => void;
} = {}) => {
  return useMutation({
    mutationFn: async (
      payload: CreatePublicDocumentLinkV1UserDocumentsDocumentIdPublicLinkPostParams,
    ) => {
      const response = await withErrorHandling(
        api.v1.createPublicDocumentLinkV1UserDocumentsDocumentIdPublicLinkPost(payload),
        {
          loading: 'Creating public document link...',
          success: 'Public document link created',
        },
      );
      return response.data;
    },
    onSuccess: data => {
      onSuccess && onSuccess(data?.document?.public_document_id);
      queryClient.invalidateQueries([DOCUMENTS_LIST_QUERY_KEY]);
      queryClient.invalidateQueries([DOCUMENTS_TREE_QK]);
      queryClient.invalidateQueries([CURRENT_USER_TASKS_ACTIVITY_LOGS_QUERY_KEY]);
    },
  });
};

export const useDeletePublicDocumentLinkMutation = ({
  onSuccess,
}: {
  onSuccess?: () => void;
} = {}) => {
  return useMutation({
    mutationFn: async (
      payload: DeletePublicDocumentLinkV1UserDocumentsDocumentIdPublicLinkDeleteParams,
    ) => {
      const response = await withErrorHandling(
        api.v1.deletePublicDocumentLinkV1UserDocumentsDocumentIdPublicLinkDelete(payload),
        {
          loading: 'Deleting public document link...',
          success: 'Public document link deleted',
        },
      );
      return response.data;
    },
    onSuccess: () => {
      onSuccess && onSuccess();
      queryClient.invalidateQueries([DOCUMENTS_LIST_QUERY_KEY]);
      queryClient.invalidateQueries([DOCUMENTS_TREE_QK]);
      queryClient.invalidateQueries([CURRENT_USER_TASKS_ACTIVITY_LOGS_QUERY_KEY]);
    },
  });
};

export const useGetPublicDocumentByIdQuery = (
  payload: GetPublicDocumentV1UserDocumentsPublicPublicDocumentIdGetParams,
  disabled = false,
) => {
  const businessId = useGetCurrentBusinessId();
  return useQuery({
    queryKey: [DOCUMENTS_LIST_QUERY_KEY, JSON.stringify(payload), businessId],
    staleTime: 1000 * 60 * 60 * 3, // 3 hours
    queryFn: async () => {
      const response = await api.v1.getPublicDocumentV1UserDocumentsPublicPublicDocumentIdGet(
        payload,
      );
      return response.data;
    },
    keepPreviousData: true,
    retry: 2,
    enabled: !disabled,
  });
};

export const useUpdateDocumentMutation = () => {
  return useMutation({
    mutationFn: async (payload: UpdateUserDocumentRequest) => {
      const response = await withErrorHandling(api.v1.updateDocumentV1UserDocumentsPatch(payload), {
        loading: 'Updating document...',
        success: 'Document updated',
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries([DOCUMENTS_LIST_QUERY_KEY]);
      queryClient.invalidateQueries([CURRENT_USER_TASKS_ACTIVITY_LOGS_QUERY_KEY]);
    },
  });
};

export const useDeleteDocumentMutation = () => {
  return useMutation({
    mutationFn: async ({ id }: { id: number }) => {
      const response = await withErrorHandling(
        api.v1.deleteDocumentV1UserDocumentsDocumentIdDelete({ documentId: id }),
        {
          loading: 'Archiving document...',
          success: 'Document archived',
        },
      );
      return response.data;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries([DOCUMENTS_LIST_QUERY_KEY]);
      await queryClient.invalidateQueries([CURRENT_USER_TASKS_ACTIVITY_LOGS_QUERY_KEY]);
    },
  });
};

export const useRestoreDocumentMutation = () => {
  return useMutation({
    mutationFn: async (id: number) => {
      const response = await withErrorHandling(
        api.v1.deleteDocumentV1UserDocumentsDocumentIdDelete({ documentId: id }),
        {
          loading: 'Restoring document...',
          success: 'Document restored',
        },
      );
      return response.data;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries([DOCUMENTS_LIST_QUERY_KEY]);
      await queryClient.invalidateQueries([CURRENT_USER_TASKS_ACTIVITY_LOGS_QUERY_KEY]);
    },
  });
};
